import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  Renderer2,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { paymentTermsModalParts } from '@app/service-request-payment-input/sub-components/modal/payment-terms/payment-terms.state';
import { PaymentMethodsType } from '@common/helpers/constants/service-request-payment-input/service-request-payment-input.constants';
import { ServicingCommonModel } from '@common/interfaces';
import { ChargeableLounge } from '@common/interfaces/mybooking/mybooking.interface';
import { ServicingCommonStoreService } from '@common/services';
import { CardinalService } from '@common/services/service-request-payment-input/cardinal.service';
import { ServiceRequestPaymentInputService } from '@common/services/service-request-payment-input/service-request-payment-input-service';
import { MasterJsonKeyPrefix } from '@conf/asw-master.config';
import { AgreementAreaParts } from '@lib/components';
import { SupportComponent } from '@lib/components/support-class';
import { DialogClickType, PageType} from '@lib/interfaces';
import { StaticMsgPipe } from '@lib/pipes';
import { CommonLibService, DialogDisplayService, ModalService, SystemDateService } from '@lib/services';
import { Subscription, combineLatest } from 'rxjs';
import { GetOrderResponseDataOrderType, Traveler } from 'src/sdk-servicing';
import { createServiceRequestPaymentInputPresModuleForm } from './service-request-payment-input-pres.form';

@Component({
  selector: 'asw-service-request-payment-input-pres',
  templateUrl: './service-request-payment-input-pres.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceRequestPaymentInputPresComponent extends SupportComponent implements AfterViewInit {
  @Input() set deviceId(value: string) {
    this.deviceIdFormGroup.get('blackBox')?.setValue(value);
    this._change.markForCheck();
  }

  // MutationObserverのID保存用
  private mutationObserver?: MutationObserver;

  _subscriptions: Subscription = new Subscription();

  public agreementAreaParams: AgreementAreaParts = {
    partsTitle: this._staticMsgPipe.transform('heading.agreement'),
    isModal: false,
    submitText: this._staticMsgPipe.transform('label.purchase1'),
  };

  deviceIdFormGroup: FormGroup;

  isDisplayAlipay: boolean = false;
  isDispalyChinaUnionPay: boolean = false;
  isDisplayCreditCard: boolean = false;
  isDisplayPayPal: boolean = false;
  isDisplayIssuerReceipt: boolean = false;
  isDisplayNotification: boolean = false;

  chargeableLounge!: ChargeableLounge;
  servicingCommon!: ServicingCommonModel;

  constructor(
    private readonly _common: CommonLibService,
    private readonly _staticMsgPipe: StaticMsgPipe,
    private readonly _modalService: ModalService,
    private readonly _dialogService: DialogDisplayService,
    private readonly _serviceRequestPaymentInputService: ServiceRequestPaymentInputService,
    private readonly _change: ChangeDetectorRef,
    private readonly _cardinalService: CardinalService,
    private readonly _servicingCommonStoreService: ServicingCommonStoreService,
    private readonly _elementRef: ElementRef,
    private readonly _renderer: Renderer2,
    private readonly _ngZone: NgZone,
    private readonly _systemDateSvc: SystemDateService
  ) {
    super(_common);
    this.deviceIdFormGroup = new FormGroup({
      blackBox: new FormControl(''),
    });
  }
  ngAfterViewInit(): void {
    // jsのonclickイベントから呼ぶメソッドの定義をwindowオブジェクトに格納する
    (window as any)['PaymentTermsFunction'] = {
      callFunction: (event?: Event) => this.openPaymentTerms(event),
    };

    // 同意エリア取得
    const agreementArea = this._elementRef.nativeElement.querySelector('asw-agreement-area');

    if (agreementArea) {
      //　MutationObserverのコールバック関数をセット
      this.mutationObserver = new MutationObserver(() => this.addFunctionToDynamicMessage());

      // MutationObserverを起動して、DOMの変更を監視する
      this.mutationObserver.observe(agreementArea, { childList: true, subtree: true, characterData: true });
    }
  }

  init(): void {
    const systemDate = this._systemDateSvc.getAirportLocalDate(
      this._common.aswContextStoreService.aswContextData.pointOfSaleId
    );
    this._serviceRequestPaymentInputService.paymentInputFG = createServiceRequestPaymentInputPresModuleForm(systemDate);
    this._serviceRequestPaymentInputService.isSubHeaderDisplayGoBack = true;
    this._subscriptions.add(
      this._servicingCommonStoreService
        .getServicingCommonModel$()
        .subscribe((servicingCommon: ServicingCommonModel) => {
          this.chargeableLounge = servicingCommon.chargeableLounge ?? ({} as ChargeableLounge);
          this.servicingCommon = servicingCommon;
        })
    );

    this._subscriptions.add(
      combineLatest([
        this._serviceRequestPaymentInputService.paymentMethod$,
        this._serviceRequestPaymentInputService.pnr$,
      ]).subscribe(([paymentMethod, pnr]) => {
        if (paymentMethod) {
          // [1.5] 支払方法：Alipay
          this.isDisplayAlipay = paymentMethod === PaymentMethodsType.ALIPAY;

          // [1.6] 支払方法：銀聯
          this.isDispalyChinaUnionPay = paymentMethod === PaymentMethodsType.UNION_PAY;

          // [1.7] 支払方法：クレジットカード
          this.isDisplayCreditCard = paymentMethod === PaymentMethodsType.CREDIT_CARD;

          // [1.8] 支払方法：Paypal
          // [7.1] ユーザ共通情報.POS国コード=”JP”(日本)の場合、静的文言のPayPalに関する案内表示する。
          this.isDisplayPayPal =
            paymentMethod === PaymentMethodsType.PAYPAL &&
            this._common.aswContextStoreService.aswContextData.posCountryCode === 'JP';
        }
        if (pnr) {
          // [1.10] 申込内容連絡先情報
          // 1.PNR情報取得API応答.data.orderType.channelType = ""gds"" (GDS PNR)、または""nh3"" (国内代理店作成PNR)かつ当該搭乗者情報.passengerTypeCode≠”INF”の場合、搭乗者の申込内容連絡先情報の入力欄を表示する。
          // 2.上記以外の場合、非表示とする。
          const hasInfantPassenger = pnr.travelers?.some(
            (traveler) => traveler.passengerTypeCode === Traveler.PassengerTypeCodeEnum.Inf
          );
          this.isDisplayNotification =
            (pnr.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Gds ||
              pnr.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Nh3) &&
            !hasInfantPassenger;
        }
      })
    );
  }

  /**
   * 支払規約リンク押下処理
   * @param event エレメントのイベント情報
   */
  openPaymentTerms(event?: Event) {
    //イベントが渡された場合、preventDefaultで遷移処理を中止する
    if (event) {
      event.preventDefault();
    }
    // メッセージキーを作成
    const messageKey: string = `label.paymentTerms.details.${this._serviceRequestPaymentInputService.paymentMethod$.value}`;

    // Pos付きでメッセージを取得
    let message = this._staticMsgPipe.transform(
      `${messageKey}.${this._common.aswContextStoreService.aswContextData.posCountryCode}`
    );

    // 取得できてない場合(キーが変換できていない)
    if (message.includes(messageKey)) {
      // posなしで取得
      message = this._staticMsgPipe.transform(`${messageKey}`);
    }

    // モーダル呼び出しパラメータの設定
    const part = paymentTermsModalParts();
    part.payload = { message };
    // モーダル表示
    this._ngZone.run(() => this._modalService.showSubModal(part));
  }

  /**
   * 動的文言内の指定IDを持つタグにイベントを設定する
   */
  addFunctionToDynamicMessage(): void {
    // 同意エリア取得
    const agreementArea = this._elementRef.nativeElement.querySelector('asw-agreement-area');

    if (agreementArea) {
      // aタグ取得
      const aTags = agreementArea.querySelectorAll('a') as HTMLAnchorElement[];

      // IDがopenPaymentTermsの場合に特定のイベントを埋め込む
      Array.from(aTags)
        .filter((a: HTMLAnchorElement) => a.id === 'openPaymentTerms')
        .forEach((a: HTMLAnchorElement) => {
          // u-payment-input-hyperlink-textのスタイルが設定されていない場合
          if (!a.classList.contains('u-payment-input-hyperlink-text')) {
            // aタグにイベントを設定する
            this._renderer.setAttribute(a, 'onclick', 'window.PaymentTermsFunction.callFunction(event)');
            // 見た目上リンクにするためクラスを付与
            a.classList.add('u-payment-input-hyperlink-text');
          }
        });
      this._change.detectChanges();
    }
  }

  // (14) 入するボタン押下処理　※同意文言エリア
  clickAgree() {
    this.paymentInputFG.markAllAsTouched();
    // (14.1) 入力チェックを行う。
    for (const name in this.paymentInputFG.controls) {
      this.paymentInputFG.get(name)?.updateValueAndValidity();
    }
    this._serviceRequestPaymentInputService.checkValid$.next(true);
    if (this.paymentInputFG.invalid) {
      return;
    }
    // ( 14.2)	以下の内容で、確認ダイアログを表示する。キャンセルの場合処理を中断し、以降の処理を行わない。
    const messageId = this.getMessageId();
    // ( 14.3) 画面.同意ボタン、画面.戻るボタンを非活性化する。
    this._dialogService
      // ( 14.2.2.4) 以下の条件でASWDBマスタの動的文言テーブルからメッセージを取得し、取得したメッセージを基に確認ダイアログ(G02-005)を表示する。
      .openDialog({ message: MasterJsonKeyPrefix.DYNAMIC + messageId })
      .buttonClick$.subscribe(async (result) => {
        const deviceId = this.deviceIdFormGroup.get('blackBox')?.value ?? '';
        if (result.clickType === DialogClickType.CONFIRM) {
          //処理実行前にエラー情報をクリアする
          this._common.errorsHandlerService.clearRetryableError(PageType.PAGE);
          this._serviceRequestPaymentInputService.isSubHeaderDisplayGoBack = false;
          this._cardinalService.excutePaymentInput(deviceId);
        } else {
          // (14.2.2.5) 確認ダイアログより閉じるボタン押下が返却された場合、処理を中断し、以降の処理は行わない。
        }
      });
  }

  param = (key: number, value: string | number, dontTranslate = false) => {
    return { key: key, value: value, dontTranslate: dontTranslate };
  };

  getMessageId(): string {
    const countryPosCode = this._common.aswContextStoreService.aswContextData.posCountryCode;
    const loungeFlag = this.chargeableLounge?.isApplicationProcessed;
    const paymentMethod = this._serviceRequestPaymentInputService.paymentMethod$.value;
    // (14.2.2.2) サービシング共通store.ラウンジ情報.申込操作中フラグ = trueの場合、以下の処理を行う。
    switch (paymentMethod) {
      // (14.2.2.2.1) 画面.支払方法選択エリア.支払方法="CD"(クレジットカード)場合、メッセージIdに"MSG0510"を設定する。
      // (14.2.2.3) 上記以外の場合、以下の処理を行う。
      // (14.2.2.3.1) 画面.支払方法選択エリア.支払方法="CD"(クレジットカード)場合、メッセージIdに"MSG1016"を設定する。
      case PaymentMethodsType.CREDIT_CARD:
        return loungeFlag ? 'MSG0510' : 'MSG1016';
      // (14.2.2.2.2) 画面.支払方法選択エリア.支払方法="AP'(Alipay)場合、メッセージIdに"MSG0511"を設定する。
      // (14.2.2.3.2) 画面.支払方法選択エリア.支払方法="AP'(Alipay)場合、メッセージIdに"MSG1714"を設定する。
      case PaymentMethodsType.ALIPAY:
        return loungeFlag ? 'MSG0511' : 'MSG1714';
      // (14.2.2.2.3) 画面.支払方法選択エリア.支払方法="PP"(PayPal)場合、メッセージIdに"MSG0643"を設定する。
      case PaymentMethodsType.PAYPAL:
        if (loungeFlag === true) {
          return 'MSG0643';
        } else {
          let messageId = '';
          // (14.2.2.3.3) 画面.支払方法選択エリア.支払方法="PP"(PayPal) かつ ユーザ共通情報.POS国コード≠"JP"(日本)場合、メッセージIdに"MSG1713"を設定する。
          if (countryPosCode !== 'JP') {
            messageId = 'MSG1713';
            // (14.2.2.3.5) 画面.支払方法選択エリア.支払方法="PP"(PayPal) かつ ユーザ共通情報.POS国コード="JP"(日本)場合、メッセージIdに"MSG1713"を設定する。
          } else if (countryPosCode === 'JP') {
            messageId = 'MSG1713';
          }
          return messageId;
        }
      // (14.2.2.2.4) 画面.支払方法選択エリア.支払方法="CU"(銀聯)場合、メッセージIdに"MSG0652"を設定する。
      // (14.2.2.3.4) 画面.支払方法選択エリア.支払方法="CU"(銀聯)場合、メッセージIdに"MSG1715"を設定する。。
      case PaymentMethodsType.UNION_PAY:
        return loungeFlag ? 'MSG0652' : 'MSG1715';
      default:
        return '';
    }
  }

  get paymentInputFG(): FormGroup {
    return this._serviceRequestPaymentInputService.paymentInputFG;
  }
  destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
    // MutationObserverが起動していれば破棄
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  reload(): void {
    /* override abstract reload function of base class */
  }
}
